import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearServiceRequestStats } from 'src/store/actions/serviceRequestStats';
import { useClientPagination } from 'src/components/CustomPagination/useClientPagination';
import {
  useReportingAPI,
  UseReportingAPIReturn,
} from 'src/SharedCustomHooks/UseReportingAPI/useReportingAPI';
import {
  IUseReportingFormStateReturn,
  useReportingFormState,
} from 'src/pages/Reporting/CustomHooks/useReportingFormState';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';
import {
  IProperty,
  OccupancyReportData,
  OccupancyReportParams,
  OccupancyReportResponse,
  ReportingFormState,
  SelectedValue,
  StandardReportParams,
} from 'src/pages/Reporting/ReportingFormTypes';
import { SelectedProperty } from 'src/pages/Reporting/Forms/OccupancyDeficiencyForm/OccupancyDeficiencyForm';
import {
  headers,
  occupancyDeficiencyHeaders,
  ReportTypeOption,
  types,
  userActivityHeaders,
  userStatusReportsHeaders,
} from 'src/pages/Reporting/constants';
import {
  fetchOccupancyDeficiencyReport,
  prepareReportParams,
} from 'src/pages/Reporting/helpers';

export interface IReportHeader {
  label: string;
  key: string;
}

export interface IUseReportingPageOrchestratorReturn {
  // Form state.
  formState: ReportingFormState;
  selectedProperties: SelectedProperty[];
  selectedReportTypeOption: ReportTypeOption | null;
  updateReportType: (nextType: string) => void;
  updateSelectedValues: (nextSelectedValues: SelectedValue[]) => void;
  updateFromDate: (nextFromDate: Date | null) => void;
  updateToDate: (nextToDate: Date | null) => void;
  updateSelectedProperties: (nextSelectedProperties: SelectedProperty[]) => void;

  // Properties data.
  properties: IProperty[] | null;
  propertyOptions: IProperty[];
  isLoadingProperties: boolean;
  propertiesError: string | null;

  // Standard reporting API state.
  reports: any[];
  feedbackReport: any[];
  isGeneratingReport: boolean;

  // Occupancy report state (separate API path).
  occupancyReportData: OccupancyReportData[];
  propertyAggregateReportData: OccupancyReportData[] | undefined;
  isLoadingOccupancyReport: boolean;

  // Derived for the active report type.
  reportHeaders: IReportHeader[];
  fileName: string;
  isGenerateReportDisabled: boolean;
  showClearResultsButton: boolean;

  // Client-side pagination for the active results table.
  paginatedTableData: any[];
  currentPage: number;
  totalPages: number;
  showPaginationControls: boolean;
  setCurrentPage: (page: number) => void;

  // Actions.
  generateReport: () => void;
  generateOccupancyReport: () => Promise<void>;
  changeReportType: (nextType: string) => void;
  clearResults: () => void;
}

const resolveReportHeaders = (reportType: string): IReportHeader[] => {
  switch (reportType) {
    case 'Reporting':
      return headers;
    case 'User Status Report':
    case 'Customer Feedback Report':
      return userStatusReportsHeaders;
    case 'OccupancyDeficiencyReport':
      return occupancyDeficiencyHeaders;
    default:
      return userActivityHeaders;
  }
};

const resolveFileName = (reportType: string): string => {
  switch (reportType) {
    case 'User Status Report':
      return 'UserStatusReport.csv';
    case 'OccupancyDeficiencyReport':
      return 'OccupancyDeficiencyReport.csv';
    default:
      return 'Report.csv';
  }
};

const resolveSelectedReportTypeOption = (
  type: string
): ReportTypeOption | null => {
  return types.find((option: ReportTypeOption) => option.value === type) ?? null;
};

const resolveIsGenerateReportDisabled = (
  formState: ReportingFormState
): boolean => {
  switch (formState.type) {
    case 'User Status Report':
      return false;
    case 'Customer Feedback Report':
      return !formState.toDate || !formState.fromDate;
    default:
      return !formState.selectedValues.length || !formState.fromDate;
  }
};

export const useReportingPageOrchestrator =
  (): IUseReportingPageOrchestratorReturn => {
    const formStateHook: IUseReportingFormStateReturn = useReportingFormState();
    const reportingAPI: UseReportingAPIReturn = useReportingAPI();

    const {
      data: properties,
      loading: isLoadingProperties,
      error: propertiesError,
      fetchProperties,
    } = useProperties();

    // Local state for occupancy report results (separate API path).
    const [occupancyReportData, setOccupancyReportData] = useState<
      OccupancyReportData[]
    >([]);
    const [propertyAggregateReportData, setPropertyAggregateReportData] =
      useState<OccupancyReportData[]>();
    const [isLoadingOccupancyReport, setIsLoadingOccupancyReport] =
      useState<boolean>(false);

    // TODO: phase 2 — migrate alongside ServiceRequestStats form modernization.
    const dispatch = useDispatch();

    useEffect(() => {
      reportingAPI.clearReportingState();
      dispatch(clearServiceRequestStats());
      setOccupancyReportData([]);
    }, []);

    useEffect(() => {
      void fetchProperties();
    }, []);

    const propertyOptions: IProperty[] = useMemo(() => {
      return properties || [];
    }, [properties]);

    const { formState } = formStateHook;

    const REPORTING_TABLE_ITEMS_PER_PAGE: number = 20;

    const activeTableData: any[] = useMemo(() => {
      if (formState.type === 'Customer Feedback Report') {
        return reportingAPI.feedbackReport;
      }

      return reportingAPI.reports;
    }, [formState.type, reportingAPI.reports, reportingAPI.feedbackReport]);

    const {
      currentPage,
      totalPages,
      paginatedItems: paginatedTableData,
      showPaginationControls,
      setCurrentPage,
      resetToFirstPage: resetTablePageToFirst,
    } = useClientPagination<any>({
      items: activeTableData,
      itemsPerPage: REPORTING_TABLE_ITEMS_PER_PAGE,
    });

    const generateReport = (): void => {
      const params: string | StandardReportParams = prepareReportParams(
        formState.type,
        formState.selectedValues,
        formState.fromDate,
        formState.toDate
      );

      switch (formState.type) {
        case 'User Status Report':
          void reportingAPI.fetchUserStatusReport(params as string);
          break;
        case 'Customer Feedback Report':
          void reportingAPI.fetchCustomerFeedbackReport(
            params as StandardReportParams
          );
          break;
        case 'Reporting':
          reportingAPI.clearReportingState();
          void reportingAPI.fetchStandardReport(params as StandardReportParams);
          break;
        case 'User Activity':
          reportingAPI.clearReportingState();
          void reportingAPI.fetchUserActivityReport(
            params as StandardReportParams
          );
          break;
        default:
          console.warn(`Unhandled report type: ${formState.type}`);
          break;
      }
    };

    const generateOccupancyReport = async (): Promise<void> => {
      setIsLoadingOccupancyReport(true);

      try {
        const listOfSelectedPropertyIds: string[] =
          formStateHook.selectedProperties.map(
            (property: SelectedProperty) => property.id
          );

        const params: OccupancyReportParams = {
          listOfSelectedPropertyIds,
        };

        const response: OccupancyReportResponse =
          await fetchOccupancyDeficiencyReport(params);

        setOccupancyReportData(response.data.unitAggregates);
        setPropertyAggregateReportData(response.data.propertyAggregates);
      } catch (error) {
        console.error('Error fetching occupancy report:', error);
      } finally {
        setIsLoadingOccupancyReport(false);
      }
    };

    const changeReportType = (nextType: string): void => {
      formStateHook.updateReportType(nextType);
      reportingAPI.clearReportingState();
      dispatch(clearServiceRequestStats());
      setOccupancyReportData([]);
      setPropertyAggregateReportData([]);
      resetTablePageToFirst();
    };

    const clearResults = (): void => {
      formStateHook.resetFormState();
      reportingAPI.clearReportingState();
      dispatch(clearServiceRequestStats());
      setOccupancyReportData([]);
      setPropertyAggregateReportData([]);
      resetTablePageToFirst();
    };

    return {
      formState: formStateHook.formState,
      selectedProperties: formStateHook.selectedProperties,
      selectedReportTypeOption: resolveSelectedReportTypeOption(formState.type),
      updateReportType: formStateHook.updateReportType,
      updateSelectedValues: formStateHook.updateSelectedValues,
      updateFromDate: formStateHook.updateFromDate,
      updateToDate: formStateHook.updateToDate,
      updateSelectedProperties: formStateHook.updateSelectedProperties,

      properties,
      propertyOptions,
      isLoadingProperties,
      propertiesError,

      reports: reportingAPI.reports,
      feedbackReport: reportingAPI.feedbackReport,
      isGeneratingReport: reportingAPI.isGeneratingReport,

      occupancyReportData,
      propertyAggregateReportData,
      isLoadingOccupancyReport,

      reportHeaders: resolveReportHeaders(formState.type),
      fileName: resolveFileName(formState.type),
      isGenerateReportDisabled: resolveIsGenerateReportDisabled(formState),
      showClearResultsButton:
        formState.selectedValues.length > 0 ||
        formStateHook.selectedProperties.length > 0 ||
        formState.fromDate !== null,

      paginatedTableData,
      currentPage,
      totalPages,
      showPaginationControls,
      setCurrentPage,

      generateReport,
      generateOccupancyReport,
      changeReportType,
      clearResults,
    };
  };
